import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useApi } from '../../hooks/useApi.js';
import { api, ApiError } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';
import { TERRACOTTA } from '../jobs/jobs-shared.js';

export const JobsWidget = () => {
  const { route } = useLocation();
  const { data, refetch } = useApi('/jobs/todo-widget');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const quickAdd = async () => {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/jobs', { title: t });
      setTitle('');
      refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = async (job) => {
    try {
      if (job.status === 'done') await api.patch(`/jobs/${job.id}`, { status: 'open' });
      else await api.post(`/jobs/${job.id}/complete`);
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed');
    }
  };

  const items = data?.items ?? [];

  return (
    <div class="rounded-md border border-border-subtle bg-bg-card p-4">
      <div class="mb-3 flex items-center justify-between">
        <span class="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TERRACOTTA }}>
          <span class="block h-2 w-2 rounded-full" style={{ background: TERRACOTTA }} />
          Jobs
        </span>
        <button type="button" onClick={() => route('/jobs')} class="text-xs text-text-muted hover:text-brand">
          View jobs →
        </button>
      </div>

      <div class="mb-3 flex items-center gap-2">
        <input
          type="text"
          value={title}
          onInput={(e) => setTitle(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') quickAdd(); }}
          placeholder="Add a to-do…"
          class="flex-1 rounded-md border border-border-strong bg-bg-base px-3 py-1.5 text-sm placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <Button variant="primary" onClick={quickAdd} disabled={busy || !title.trim()}>
          {busy ? '…' : 'Add'}
        </Button>
      </div>
      {error && <p class="m-0 mb-2 text-[12px] font-semibold text-ink-magenta">{error}</p>}

      {items.length === 0 ? (
        <p class="m-0 text-[12px] text-text-tertiary">Nothing this week.</p>
      ) : (
        <ul class="m-0 space-y-1 p-0">
          {items.map((job) => (
            <li key={job.id} class="flex list-none items-center gap-2">
              <input
                type="checkbox"
                checked={job.status === 'done'}
                onChange={() => toggleDone(job)}
                class="h-4 w-4 rounded border-border-strong"
                style={{ accentColor: TERRACOTTA }}
              />
              <button
                type="button"
                onClick={() => route(`/jobs/${job.id}`)}
                class={`min-w-0 flex-1 truncate text-left text-[13px] ${job.status === 'done' ? 'text-text-muted line-through' : 'text-text-body'}`}
              >
                {job.title}
              </button>
              <span class="shrink-0 font-mono text-[11px] text-text-tertiary">{job.jobNumber}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
